import { fabric } from 'fabric';
import {
  FILL,
  STROKE,
  TEXT,
} from '../../../components/Widgets/constants/shapes';
import { useCallback, useMemo, useState } from 'react';
import { useEditorData } from '../providers/EditorProvider';
import { handleVideo, VideoModeEnum } from '../utils/handleVideo';

interface AddVideoOptions {
  url: string;
  fileType: string;
  assetId: string;
  thumbnailUrl?: string;
}

export interface FabricJSEditor {
  canvas: fabric.Canvas;
  deleteAll: () => void;
  deleteSelected: () => void;
  fillColor: string;
  strokeColor: string;
  setFillColor: (color: string) => void;
  setStrokeColor: (color: string) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  addVideo: (options: AddVideoOptions) => void;
}

/**
 * Creates editor
 */
const buildEditor = (
  canvas: fabric.Canvas,
  fillColor: string,
  strokeColor: string,
  _setFillColor: (color: string) => void,
  _setStrokeColor: (color: string) => void,
  scaleStep: number,
): FabricJSEditor => {
  return {
    canvas,
    deleteAll: () => {
      canvas.getObjects().forEach((object) => canvas.remove(object));
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    },
    deleteSelected: () => {
      canvas.getActiveObjects().forEach((object) => canvas.remove(object));
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    },
    fillColor,
    strokeColor,
    setFillColor: (fill: string) => {
      _setFillColor(fill);
      canvas.getActiveObjects().forEach((object) => object.set({ fill }));
      canvas.requestRenderAll();
    },
    setStrokeColor: (stroke: string) => {
      _setStrokeColor(stroke);
      canvas.getActiveObjects().forEach((object) => {
        if (object.type === TEXT.type) {
          // use stroke in text fill
          object.set({ fill: stroke });
          return;
        }
        object.set({ stroke });
      });
      canvas.requestRenderAll();
    },
    zoomIn: () => {
      const zoom = canvas.getZoom();
      canvas.setZoom(zoom / scaleStep);
    },
    zoomOut: () => {
      const zoom = canvas.getZoom();
      canvas.setZoom(zoom * scaleStep);
    },
    addVideo: async ({ url, fileType, assetId, thumbnailUrl }) => {
      await handleVideo({
        canvas,
        url,
        fileType,
        mode: VideoModeEnum.Thumbnail,
        assetId,
        thumbnailUrl,
      });
    },
  };
};

// `selectedObjects` was confirmed unused by every caller of useFabricEditor;
// it is kept only as a stable empty array for return-shape compatibility.
const selectedObjects: fabric.Object[] = [];

interface FabricJSEditorState {
  editor?: FabricJSEditor;
}

interface FabricJSEditorHook extends FabricJSEditorState {
  selectedObjects?: fabric.Object[];
  canvas?: fabric.Canvas | null;
  onReady: (canvas: fabric.Canvas) => void;
}

interface FabricJSEditorHookProps {
  defaultFillColor?: string;
  defaultStrokeColor?: string;
  scaleStep?: number;
}

const useFabricEditor = (
  props: FabricJSEditorHookProps = {},
): FabricJSEditorHook => {
  const scaleStep = props.scaleStep || 0.5;
  const { defaultFillColor, defaultStrokeColor } = props;
  const { canvas, setCanvas } = useEditorData();

  const [fillColor, setFillColor] = useState<string>(defaultFillColor || FILL);
  const [strokeColor, setStrokeColor] = useState<string>(
    defaultStrokeColor || STROKE,
  );
  // The render loop and selection:* listeners that used to live here were
  // registered independently by every single component that calls this
  // hook (Toolbar, FooterBar, CanvasBody, every Widget panel, etc. - all
  // share the same `canvas` instance via EditorProvider's context), with no
  // cleanup at all. That meant every mounted consumer spawned its own
  // never-cancelled 60fps `canvas.renderAll()` requestAnimFrame loop
  // running concurrently and forever, which is the CPU cost this used to
  // carry. The selection listeners now live once, centrally, in
  // EditorProvider, and rendering is on demand.
  // The editor object is memoized: effects that depend on it (arrow keys,
  // CanvasBody) would otherwise re-run on every render of every consumer.
  const onReady = useCallback(
    (canvasReady: fabric.Canvas): void => {
      // https://github.com/fabricjs/fabric.js/wiki/FAQ#how-can-i-change-the-order-of-objects-on-the-canvas
      canvasReady.preserveObjectStacking = true;
      setCanvas(canvasReady);
    },
    [setCanvas],
  );
  const editor = useMemo(
    () =>
      canvas
        ? buildEditor(
            canvas,
            fillColor,
            strokeColor,
            setFillColor,
            setStrokeColor,
            scaleStep,
          )
        : undefined,
    [canvas, fillColor, strokeColor, scaleStep],
  );

  return {
    selectedObjects,
    onReady,
    canvas,
    editor,
  };
};

export { useFabricEditor };
